import { DuprProduct } from '@/services/checkout'

// Utility functions for pricing logic and data transformation

export interface PricingInfo {
  hasFreeTrial: boolean
  trialDays: number
  hasDiscount: boolean
  priceReduction: number
  originalPrice: string
  period: string
  billingStartDate: Date
}

/**
 * Analyzes a product to determine pricing display information
 */
export const analyzePricing = (product: DuprProduct, locale = 'en-US'): PricingInfo => {
  const currencyFormat = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: 'USD',
    currencyDisplay: 'symbol',
  })

  const trialDays = product.promotion?.duprDiscount?.trialDays || 0
  const priceReduction = product.promotion?.duprDiscount?.priceReduction || 0
  const hasFreeTrial = trialDays > 0
  const hasDiscount = priceReduction > 0

  const originalPrice = currencyFormat.format(product.price / 100)
  const period = product.period === 'P12M' ? 'year' : 'month'
  
  // Calculate when billing starts (if there's a free trial)
  const billingStartDate = new Date()
  if (hasFreeTrial) {
    billingStartDate.setDate(billingStartDate.getDate() + trialDays)
  }

  return {
    hasFreeTrial,
    trialDays,
    hasDiscount,
    priceReduction,
    originalPrice,
    period,
    billingStartDate
  }
}

/**
 * Determines if a product has any promotional pricing
 */
export const hasPromotion = (product: DuprProduct): boolean => {
  const pricing = analyzePricing(product)
  return pricing.hasFreeTrial || pricing.hasDiscount
}

/**
 * Gets the benefactor name if available
 */
export const getBenefactorName = (product: DuprProduct): string | null => {
  const name = product.promotion?.benefactor?.displayName
  return name && name.trim() ? name.trim() : null
}

/**
 * Formats the billing start date for display
 */
export const formatBillingDate = (date: Date, locale = 'en-US'): string => {
  const formatter = new Intl.DateTimeFormat(locale, { 
    month: 'short', 
    day: 'numeric'
  })
  return formatter.format(date)
}

/**
 * Transforms products for rendering, grouping by benefactor
 */
export const groupProductsByBenefactor = (products: DuprProduct[]): {
  benefactor: string | null
  products: DuprProduct[]
}[] => {
  const grouped = products.reduce((acc, product) => {
    const benefactor = getBenefactorName(product)
    const key = benefactor || 'no-benefactor'
    
    if (!acc[key]) {
      acc[key] = {
        benefactor,
        products: []
      }
    }
    
    acc[key].products.push(product)
    return acc
  }, {} as Record<string, { benefactor: string | null, products: DuprProduct[] }>)

  return Object.values(grouped)
}

/**
 * Parses period string (like "P1Y", "P12M", "P1M", "P1W") into readable format
 */
export const parsePeriod = (period: string): { months: number; displayName: string; displayNameLong: string; isOneTime: boolean } => {
  // Handle one-time purchase (P0D)
  if (period === 'P0D') {
    return { months: 0, displayName: '', displayNameLong: 'one-time purchase', isOneTime: true }
  }

  // Handle year format (P1Y, P2Y, etc.)
  const yearMatch = period.match(/P(\d+)Y/)
  if (yearMatch) {
    const years = parseInt(yearMatch[1])
    const months = years * 12
    return {
      months,
      displayName: years === 1 ? 'year' : 'years',
      displayNameLong: years === 1 ? 'yearly' : `${years} years`,
      isOneTime: false
    }
  }

  // Handle week format (P1W, P2W, etc.)
  const weekMatch = period.match(/P(\d+)W/)
  if (weekMatch) {
    const weeks = parseInt(weekMatch[1])
    const months = Math.round(weeks / 4.33) // Average weeks per month
    return {
      months,
      displayName: weeks === 1 ? 'week' : 'weeks',
      displayNameLong: weeks === 1 ? 'weekly' : `${weeks} weeks`,
      isOneTime: false
    }
  }

  // Handle month format (P12M, P1M, etc.)
  const monthMatch = period.match(/P(\d+)M/)
  const months = monthMatch ? parseInt(monthMatch[1]) : 1

  if (months >= 12) {
    const years = months / 12
    return {
      months,
      displayName: years === 1 ? 'year' : 'years',
      displayNameLong: years === 1 ? 'yearly' : `${years} years`,
      isOneTime: false
    }
  }

  if (months === 3) {
    return {
      months,
      displayName: 'quarter',
      displayNameLong: 'quarterly',
      isOneTime: false
    }
  }

  return {
    months,
    displayName: months === 1 ? 'month' : 'months',
    displayNameLong: months === 1 ? 'monthly' : `${months} months`,
    isOneTime: false
  }
}

/**
 * Determines the primary call-to-action text based on product pricing
 */
export const getPrimaryPricingText = (product: DuprProduct): string => {
  const pricing = analyzePricing(product)
  
  if (pricing.hasFreeTrial) {
    return `FREE for ${pricing.trialDays} days`
  }
  
  // For non-trial products, show price with period (e.g., "$9.99/month")
  const periodInfo = parsePeriod(product.period)
  return `${pricing.originalPrice}/${periodInfo.displayName}`
}

/**
 * Gets secondary pricing text for standard products (period and billing)
 * @deprecated - No longer used since we show price/period format directly
 */
export const getSecondaryPricingText = (product: DuprProduct): string => {
  const periodInfo = parsePeriod(product.period)
  return `Billed ${periodInfo.displayNameLong}`
}

/**
 * Checks if two products are equivalent for selection purposes
 */
export const areProductsEqual = (a: DuprProduct, b: DuprProduct): boolean => {
  return a.productId === b.productId
}